import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, ExternalLink, Link2, MapPin, Globe, Shapes, Users } from "lucide-react";
import { card, theme, type as text } from "../theme";
import { fmtDate } from "../lib/format";
import { api, ApiError } from "../api/client";
import { PortalLink } from "./PortalLink";
import {
  entityRef, useEntityTagData, KIND_LABELS, KIND_HREFS,
  type EntityRef, type EntityKind, type RelatedEntity, type TagDimension,
} from "./EntityTags";

// ── Related records ───────────────────────────────────────────────────────────
//
// One card on every register's detail view answering "what else touches this?".
// Two sources, shown in this order:
//
//   Linked   — the record's own foreign keys (the risk it raised, the supplier it
//              names, the assets it hit), passed in by the page because only the
//              page knows its columns.
//   Filed the same way — every row sharing a department, office, jurisdiction
//              or type tag (GET /tags/related/<ref>), grouped by kind.
//
// The tag endpoint is client / consultant only; an auditor (403) sees the
// explicit links and nothing else.

export interface RelatedLink {
  label: string;
  href?: string | null;
  kindLabel: string;
}

const DIM_ICON: Record<TagDimension, React.ReactNode> = {
  department: <Users size={10} />,
  site: <MapPin size={10} />,
  jurisdiction: <Globe size={10} />,
  type: <Shapes size={10} />,
};

const sectionLabel: React.CSSProperties = { ...text.eyebrow, color: theme.color.faint, margin: "10px 0 4px" };

const rowStyle: React.CSSProperties = {
  display: "flex", alignItems: "center", gap: 8, padding: "4px 0", textDecoration: "none", flexWrap: "wrap", minWidth: 0,
};

const kindStyle: React.CSSProperties = { ...text.meta, fontWeight: 700, minWidth: 96 };

function Row({ kindLabel, label, hint, href, right }: { kindLabel: string; label: string; hint?: string; href?: string | null; right?: React.ReactNode }) {
  const body = (
    <>
      <span style={kindStyle}>{kindLabel}</span>
      <span style={{ fontSize: text.hint.fontSize, color: href ? theme.color.accentText : theme.color.text, fontWeight: 500, minWidth: 0, overflowWrap: "anywhere" }}>{label}</span>
      {hint && <span style={text.meta}>{hint}</span>}
      <span style={{ marginLeft: "auto", display: "flex", gap: 4, flexWrap: "wrap", alignItems: "center" }}>
        {right}
        {href && <ExternalLink size={11} style={{ color: theme.color.accent, flexShrink: 0 }} />}
      </span>
    </>
  );
  return href ? <PortalLink to={href} style={rowStyle}>{body}</PortalLink> : <div style={rowStyle}>{body}</div>;
}

export function RelatedRecords({ entity, links, defaultOpen = true }: {
  /** The row itself; null for a record that cannot be tagged (only its own links are shown). */
  entity: EntityRef | null;
  links?: RelatedLink[];
  defaultOpen?: boolean;
}) {
  const data = useEntityTagData();
  const ref = useMemo(() => (entity ? entityRef(entity) : null), [entity?.kind, entity?.id]);
  const [rows, setRows] = useState<RelatedEntity[] | null>(null);
  const [forbidden, setForbidden] = useState(false);
  const [open, setOpen] = useState(defaultOpen);
  const [showAll, setShowAll] = useState(false);

  useEffect(() => {
    if (!ref) { setRows([]); return; }
    let live = true;
    api.get<{ related: RelatedEntity[] }>(`/tags/related/${encodeURIComponent(ref)}`)
      .then((d) => { if (live) setRows(d.related ?? []); })
      .catch((e) => {
        if (!live) return;
        if (e instanceof ApiError && e.status === 403) setForbidden(true);
        setRows([]);
      });
    return () => { live = false; };
    // Re-read when this row's own tags change, so tagging it connects it at once.
  }, [ref, ref ? data.tags[ref] : null]);

  const explicit = (links ?? []).filter((l) => !!l.label);
  if (forbidden && explicit.length === 0) return null;

  const valueLabel = (s: { dimension: TagDimension; value: string }) => {
    if (s.dimension === "department") return data.departments.find((d) => d.id === s.value)?.name ?? "department";
    if (s.dimension === "site") return data.sites.find((x) => x.id === s.value)?.name ?? "office";
    if (s.dimension === "type") return data.types.find((t) => t.value === s.value)?.label ?? "type";
    return s.value;
  };

  const tagged = forbidden ? [] : (rows ?? []);
  const limit = 12;
  const shown = showAll ? tagged : tagged.slice(0, limit);
  const groups = new Map<EntityKind, RelatedEntity[]>();
  for (const r of shown) {
    const list = groups.get(r.kind) ?? [];
    list.push(r);
    groups.set(r.kind, list);
  }
  const total = explicit.length + tagged.length;

  return (
    <div style={{ ...card, padding: "10px 14px", marginTop: 12 }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        style={{ display: "flex", alignItems: "center", gap: 6, width: "100%", background: "none", border: "none", padding: 0, cursor: "pointer", ...text.sectionTitle, margin: 0, fontFamily: "inherit" }}
      >
        {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        <Link2 size={14} style={{ color: theme.color.accent }} />
        Related records
        <span style={{ ...text.meta, fontWeight: 600, color: theme.color.muted, marginLeft: 4 }}>
          {rows === null && !forbidden ? "" : `(${total})`}
        </span>
      </button>

      {open && (
        <div style={{ marginTop: 4 }}>
          {explicit.length > 0 && (
            <>
              <div style={sectionLabel}>Linked</div>
              {explicit.map((l, i) => <Row key={`${l.kindLabel}-${l.label}-${i}`} kindLabel={l.kindLabel} label={l.label} href={l.href} />)}
            </>
          )}

          {!forbidden && rows === null && <div style={{ ...text.hint, color: theme.color.faint, marginTop: 8 }}>Looking for related records…</div>}

          {!forbidden && rows !== null && tagged.length === 0 && (ref || explicit.length === 0) && (
            <div style={{ ...text.hint, color: theme.color.faint, marginTop: 8 }}>
              {ref ? "Nothing is filed the same way yet — tag this record to connect it." : "Nothing linked yet."}
            </div>
          )}

          {!forbidden && tagged.length > 0 && (
            <>
              <div style={sectionLabel}>Filed the same way</div>
              {Array.from(groups.entries()).map(([kind, list]) => (
                <div key={kind} style={{ marginBottom: 4 }}>
                  {list.map((r) => (
                    <Row
                      key={r.ref}
                      kindLabel={KIND_LABELS[r.kind] ?? r.kind}
                      label={r.label}
                      hint={r.hint}
                      href={KIND_HREFS[r.kind]?.(r.id) ?? null}
                      right={Array.from(new Map(r.shared.map((s) => [`${s.dimension}:${s.value}`, s])).values()).slice(0, 3).map((s) => (
                        <span key={`${s.dimension}:${s.value}`} title={`Shares this ${s.dimension === "site" ? "office" : s.dimension}`} style={{
                          display: "inline-flex", alignItems: "center", gap: 3, padding: "1px 7px", borderRadius: 3, fontSize: 10, fontWeight: 600,
                          background: theme.color.panelAlt, color: theme.color.muted, border: `1px solid ${theme.color.border}`,
                        }}>
                          {DIM_ICON[s.dimension]}{valueLabel(s)}
                        </span>
                      ))}
                    />
                  ))}
                </div>
              ))}
              {tagged.length > limit && (
                <button type="button" onClick={() => setShowAll(!showAll)} style={{ ...text.meta, marginTop: 4, color: theme.color.accentText, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                  {showAll ? "Show fewer" : `Show all ${tagged.length}`}
                </button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Corrective actions raised against a record ───────────────────────────────
//
// A corrective action carries its own riskId / incidentId / supplierId /
// assetId (routes/improvement.routes.ts), so the register row it fixes can list
// it without a column of its own. One read of the improvement register, filtered
// here; an organisation holds tens of actions, not thousands.

export interface LinkedAction { id: string; title: string; status: string; dueDate: string | null }

export function useLinkedActions(field: "riskId" | "incidentId" | "supplierId" | "assetId", id: string | null | undefined): LinkedAction[] {
  const [actions, setActions] = useState<LinkedAction[]>([]);
  useEffect(() => {
    if (!id) { setActions([]); return; }
    let live = true;
    api.get<{ actions: Array<LinkedAction & Record<string, unknown>> }>("/improvement/actions")
      .then((d) => { if (live) setActions((d.actions ?? []).filter((a) => a[field] === id).map((a) => ({ id: a.id, title: a.title, status: a.status, dueDate: a.dueDate }))); })
      .catch(() => { if (live) setActions([]); });
    return () => { live = false; };
  }, [field, id]);
  return actions;
}

/** The actions as Linked rows, status in the label so an open one stands out. */
export function actionLinks(actions: LinkedAction[]): RelatedLink[] {
  return actions.map((a) => ({
    kindLabel: "Corrective action",
    label: `${a.title} (${a.status.replace(/_/g, " ")}${a.dueDate ? `, due ${fmtDate(a.dueDate)}` : ""})`,
    href: KIND_HREFS.action(a.id),
  }));
}

/** "RISK-007 Title" — the reference the risk register shows. */
export function riskLabel(r: { displayId: number | null; title: string }): string {
  return `${r.displayId != null ? `RISK-${String(r.displayId).padStart(3, "0")} ` : ""}${r.title}`;
}
